import { useCallback, useMemo } from "react";
import {
    FORM_1099_BOX_OPTIONS,
    Form1099BoxFilterValue,
} from "@common/constants/domains/accounting/form1099Box";
import {
    Form1099ReportFilters,
    form1099ReportFiltersSchema,
} from "@common/schemas/domains/tax/form1099-report.schema";
import { JournalEntrySourceFilter } from "@common/schemas/domains/accounting/journalEntry";
import { FiltersTypes } from "@common/types/filters/general";
import { Form1099NecView } from "@common/types/views/form1099NecView";
import { useChartOfAccountsQuery } from "@api/accountingReports.api";
import {
    activeFiltersOrder,
    FiltersChangeFn,
    FilterTabs,
    isActive,
} from "@components/pageHeader/filters/lib";

export const FORM_1099_NEC_AVAILABLE_FILTERS = [
    FilterTabs.FORM_1099_BOX,
    FilterTabs.CLASS,
    FilterTabs.LEDGER,
    FilterTabs.SPLIT_ACCOUNT,
    FilterTabs.JOURNAL_ENTRY_SOURCES,
    FilterTabs.AMOUNT,
    FilterTabs.COUNTERPARTY,
];

export const FORM_1099_NEC_SOURCE_OPTIONS = [
    JournalEntrySourceFilter.Transaction,
    JournalEntrySourceFilter.ManualJournalEntry,
    JournalEntrySourceFilter.QuickBooksOnline,
    JournalEntrySourceFilter.QuickBooksDesktop,
    JournalEntrySourceFilter.Xero,
    JournalEntrySourceFilter.MigratedData,
];

export const FORM_1099_NEC_BOX_OPTIONS: Form1099BoxFilterValue[] =
    FORM_1099_BOX_OPTIONS.map((option) => option.value);

export const FORM_1099_NEC_FILTER_TAB_LABELS: Partial<
    Record<FilterTabs, string>
> = {
    [FilterTabs.LEDGER]: "Account",
    [FilterTabs.JOURNAL_ENTRY_SOURCES]: "Source",
    [FilterTabs.COUNTERPARTY]: "Vendor",
};

interface Params {
    view: Form1099NecView;
    onViewChange: (view: Form1099NecView) => void;
    entityId: number | undefined;
}

interface Result {
    headerFilters: FiltersTypes;
    reportFilters: Form1099ReportFilters | undefined;
    isLoadingReportFilters: boolean;
    hasActiveFilters: boolean;
    onFiltersChange: FiltersChangeFn;
}

function keepEntityAccountIds(
    accountIds: string[] | undefined,
    entityAccountIds: ReadonlySet<string>,
): string[] | undefined {
    const kept = accountIds?.filter((accountId) =>
        entityAccountIds.has(accountId),
    );
    return kept?.length ? kept : undefined;
}

export function useForm1099NecFilters({
    view,
    onViewChange,
    entityId,
}: Params): Result {
    const { data: chartOfAccounts, isError: isChartOfAccountsError } =
        useChartOfAccountsQuery();
    const entityAccountIds = useMemo(
        () =>
            entityId === undefined || !chartOfAccounts
                ? undefined
                : new Set(
                      chartOfAccounts.accounts
                          .filter((account) => account.entityId === entityId)
                          .map((account) => account.id),
                  ),
        [chartOfAccounts, entityId],
    );
    const entityFilters = useMemo<FiltersTypes>(
        () =>
            entityAccountIds
                ? {
                      ...view.filters,
                      coaAccounts: keepEntityAccountIds(
                          view.filters.coaAccounts,
                          entityAccountIds,
                      ),
                      splitCoaAccounts: keepEntityAccountIds(
                          view.filters.splitCoaAccounts,
                          entityAccountIds,
                      ),
                  }
                : view.filters,
        [view.filters, entityAccountIds],
    );
    const headerFilters = useMemo<FiltersTypes>(
        () => ({
            ...entityFilters,
            entityIds: entityId === undefined ? undefined : [entityId],
        }),
        [entityFilters, entityId],
    );
    const isLoadingReportFilters =
        entityId !== undefined &&
        !entityAccountIds &&
        !isChartOfAccountsError &&
        (Boolean(view.filters.coaAccounts?.length) ||
            Boolean(view.filters.splitCoaAccounts?.length));
    const parsedFilters = useMemo(
        () => form1099ReportFiltersSchema.parse(entityFilters),
        [entityFilters],
    );
    const hasActiveFilters = useMemo(
        () =>
            activeFiltersOrder.some((filter) =>
                isActive[filter](parsedFilters),
            ),
        [parsedFilters],
    );
    const reportFilters = isLoadingReportFilters ? undefined : parsedFilters;
    const onFiltersChange = useCallback<FiltersChangeFn>(
        (payload) =>
            onViewChange({
                ...view,
                filters: { ...view.filters, ...payload },
            }),
        [view, onViewChange],
    );

    return {
        headerFilters,
        reportFilters,
        isLoadingReportFilters,
        hasActiveFilters,
        onFiltersChange,
    };
}
